/** API canónica de kits bajo rincón (KBR*) — ya no se usa para forzar GLB. */
export const TPC_PLANNER_API_BASE = 'https://planificador3dapi.cocinas-tpc.com'

/** idUnidades resueltos vía API TPC (legado; ya no redirige peticiones). */
const tpcResolvedUnitIds = new Set<number>()

/**
 * Normaliza un código de catálogo quitando prefijo PE y sufijos de acabado/gola.
 * Ej: "PE KBRB8010045 Li GO" → "KBRB8010045"
 */
export function normalizeCatalogCode(catalogID: string | number | null | undefined): string {
  let code = String(catalogID ?? '')
    .trim()
    .toUpperCase()
  if (!code) return ''
  if (code.startsWith('PE ')) code = code.slice(3).trim()
  code = code.replace(/\s+(LI|LA|RU|R2)(\s+GO)?$/i, '').replace(/\s+GO$/i, '').trim()
  return code
}

/**
 * Kits bajo rincón clásicos (KBRB/KBRP…). Política de mallas Mesh001_2 / Alto.
 * No incluye Gola (KGBRB*): ahí Mesh*_2 es Gola/Armazón, no el zócalo L.
 */
export function isKBRCatalog(catalogID: string | number | null | undefined): boolean {
  return normalizeCatalogCode(catalogID).startsWith('KBR')
}

/**
 * KBR* y Gola bajo rincón (KGBRB8012060 GO). Misma regleta/sidebar/zócalo tira;
 * el GLB Gola no usa la convención de mallas del KBR.
 */
export function isKbrRinconeroFamilyCatalog(
  catalogID: string | number | null | undefined,
): boolean {
  const code = normalizeCatalogCode(catalogID)
  return code.startsWith('KBR') || code.startsWith('KGBR')
}

/**
 * Rinconero Gola (KGBRB* / KGBRP*…): Fondo = puerta, scaleZ y zócalo L nuevos.
 * KBRB8010040 y resto KBR* (aunque lleven GO de acabado) siguen la lógica clásica.
 */
export function isKbrGolaRinconeroCatalog(
  catalogID: string | number | null | undefined,
): boolean {
  return normalizeCatalogCode(catalogID).startsWith('KGBR')
}

/**
 * KBR* cuyo GLB es solo cuerpo desde Y=0 (p.ej. KBRB8010040 TPC_1_10_1: alto API 800,
 * cuerpo 0–800). Va como KGBR/KBB: lift 100 sobre la tira y store = cuerpo.
 * Los KBR modelados con el cuerpo sobre el hueco (Y 100–900, alto API 900) siguen
 * con lift 0. Sin medida (`kbrGlbBodyMinYMm` ausente) se conserva la regla antigua.
 */
export function kbrGlbBodySitsOnFloor(
  item:
    | { catalogID?: string | number; codigo?: string | number; kbrGlbBodyMinYMm?: number }
    | null
    | undefined,
): boolean {
  if (!item) return false
  if (!isKBRCatalog(item.catalogID) && !isKBRCatalog(item.codigo)) return false
  const minY = Number(item.kbrGlbBodyMinYMm)
  return Number.isFinite(minY) && minY < 50
}

/** KARB* (no usar includes('arb'): KBRB también lo contiene). */
export function isKARBCatalog(catalogID: string | number | null | undefined): boolean {
  return normalizeCatalogCode(catalogID).startsWith('KARB')
}

/**
 * Regleta para geometría/snap = valor sidebar/API.
 * KBR/KARB: directa (20→20). Antes KBR invertía max+min−v (20↔120).
 */
export function getRegletaEffectiveMm(
  item: {
    regleta?: number | null
    catalogID?: string | number
    morphs?: Array<{ name: string; min: number; max: number }>
  } | null | undefined,
): number {
  const stored = Number(item?.regleta ?? 0)
  if (!Number.isFinite(stored) || stored <= 0) return 0
  if (isKbrRinconeroFamilyCatalog(item?.catalogID) || isKARBCatalog(item?.catalogID)) return stored
  const morph = item?.morphs?.find((m) => m.name === 'Regleta')
  if (!morph || !(Number(morph.max) > Number(morph.min))) return stored
  if (stored >= morph.max) return morph.min
  if (stored <= morph.min) return morph.max
  return morph.max + morph.min - stored
}

/** Proyectos antiguos: la regleta KBR se interpreta 20 mm más corta de lo actual. */
export const KBR_LEGACY_REGLETA_EXTRA_MM = 20

type KbrRegletaItem = {
  catalogID?: string | number
  codigo?: string | number
  referencia?: string | number
  regleta?: number | null
  regletaMinimo?: number | null
  regletaMaximo?: number | null
  kbrLegacyRegletaPlus20Applied?: boolean
  morphs?: Array<{
    name?: string
    default?: number
    _default?: number
    min?: number
    max?: number
  }>
}

/**
 * JSON antiguo: alto = cuerpo + zócalo (900). Catálogo actual = solo cuerpo (800).
 * El zócalo hijo aporta los 100 mm; el store debe quedar en alto de unidad.
 */
export const KBR_LEGACY_ZOCALO_LIFT_MM = 100

type KbrAltoItem = {
  catalogID?: string | number
  codigo?: string | number
  referencia?: string | number
  height?: number
  morphs?: Array<{
    name?: string
    default?: number
    _default?: number
    min?: number
    max?: number
  }>
  freeDimensionCatalogMm?: { x?: number; y?: number; z?: number }
}

function kbrMm(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0
}

/**
 * Alto de cuerpo (mm) embebido en el código: KBRB8010045 → 800, KGBRB8012060 → 800.
 */
function kbrBodyAltoMmFromCode(catalogID: string | number | null | undefined): number {
  const code = normalizeCatalogCode(catalogID)
  if (!code) return 0
  const match = code.match(/^KG?BR[A-Z]*(\d{2})/)
  return kbrMm(match ? Number(match[1]) * 10 : 0)
}

function resolveKbrCatalogBodyAltoMm(
  item: KbrAltoItem,
  catalogAltoMm?: number,
): number {
  const fromApi = kbrMm(catalogAltoMm) || kbrMm(item.freeDimensionCatalogMm?.y)
  const fromCode =
    kbrBodyAltoMmFromCode(item.catalogID) ||
    kbrBodyAltoMmFromCode(item.codigo) ||
    kbrBodyAltoMmFromCode(item.referencia)
  // Catálogo persistido todavía como mueble completo (900) mientras el código es cuerpo (800).
  if (
    fromApi > 0 &&
    fromCode > 0 &&
    Math.abs(fromApi - (fromCode + KBR_LEGACY_ZOCALO_LIFT_MM)) <= 2
  ) {
    return fromCode
  }
  return fromApi || fromCode
}

/**
 * Al abrir un JSON con zócalo embebido en el GLB (zona "Zocalo", no tira
 * "Zocalo 4000"): 900 (cuerpo+zócalo) → 800 (cuerpo de catálogo).
 * No pisa un Alto que el usuario hubiera cambiado (p.ej. 1000). Idempotente si ya es 800.
 * El caller debe usarlo solo en proyectos antiguos (sin hijo Zocalo 4000).
 */
export function applyKbrLegacyAltoCuerpoMasZocalo(
  item: KbrAltoItem | null | undefined,
  catalogAltoMm?: number,
): boolean {
  if (!item) return false
  if (
    !isKbrRinconeroFamilyCatalog(item.catalogID) &&
    !isKbrRinconeroFamilyCatalog(item.codigo) &&
    !isKbrRinconeroFamilyCatalog(item.referencia)
  ) {
    return false
  }

  const liveH = kbrMm(item.height)
  const bodyAlto = resolveKbrCatalogBodyAltoMm(item, catalogAltoMm)
  if (!(liveH > 0) || !(bodyAlto > 0)) return false
  if (Math.abs(liveH - (bodyAlto + KBR_LEGACY_ZOCALO_LIFT_MM)) > 2) return false
  if (liveH === bodyAlto) return false

  item.height = bodyAlto
  const morph = item.morphs?.find((m) => m.name === 'Alto')
  if (morph) {
    const wasFixed = kbrMm(morph.min) === liveH && kbrMm(morph.max) === liveH
    morph.default = bodyAlto
    morph._default = bodyAlto
    if (wasFixed) {
      morph.min = bodyAlto
      morph.max = bodyAlto
    }
  }
  if (item.freeDimensionCatalogMm && kbrMm(item.freeDimensionCatalogMm.y) === liveH) {
    item.freeDimensionCatalogMm.y = bodyAlto
  }
  return true
}

/**
 * Dato malo en BD: KGBR* guardados con fondo 720. El valor correcto es 600.
 * Quitar este bloque cuando ya no queden proyectos antiguos con 720.
 */
export const KGBR_LEGACY_FONDO_MM = 720
export const KGBR_CORRECT_FONDO_MM = 600

type KgbrFondoItem = {
  catalogID?: string | number
  codigo?: string | number
  referencia?: string | number
  thickness?: number
  morphs?: Array<{
    name?: string
    default?: number
    _default?: number
  }>
  freeDimensionCatalogMm?: { x?: number; y?: number; z?: number }
}

function kgbrFondoMm(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? Math.round(n) : 0
}

/**
 * Solo KGBR con fondo exactamente 720 → 600 (store + morph Fondo + catálogo).
 * Cualquier otra medida se deja. Idempotente al reabrir si ya está en 600.
 */
export function applyKgbrLegacyFondo720To600(item: KgbrFondoItem | null | undefined): boolean {
  if (!item) return false
  if (
    !isKbrGolaRinconeroCatalog(item.catalogID) &&
    !isKbrGolaRinconeroCatalog(item.codigo) &&
    !isKbrGolaRinconeroCatalog(item.referencia)
  ) {
    return false
  }
  if (kgbrFondoMm(item.thickness) !== KGBR_LEGACY_FONDO_MM) return false

  item.thickness = KGBR_CORRECT_FONDO_MM
  const morph = item.morphs?.find((m) => m.name === 'Fondo')
  if (morph) {
    morph.default = KGBR_CORRECT_FONDO_MM
    morph._default = KGBR_CORRECT_FONDO_MM
  }
  if (
    item.freeDimensionCatalogMm &&
    kgbrFondoMm(item.freeDimensionCatalogMm.z) === KGBR_LEGACY_FONDO_MM
  ) {
    item.freeDimensionCatalogMm.z = KGBR_CORRECT_FONDO_MM
  }
  return true
}

/**
 * Al abrir un JSON antiguo: 60 → 80 (y cualquier valor +20, clamp al max API).
 * Idempotente (`kbrLegacyRegletaPlus20Applied`). No aplicar al dibujar unidades nuevas.
 */
export function applyKbrLegacyRegletaPlus20(item: KbrRegletaItem | null | undefined): boolean {
  if (!item || item.kbrLegacyRegletaPlus20Applied) return false
  if (
    !isKBRCatalog(item.catalogID) &&
    !isKBRCatalog(item.codigo) &&
    !isKBRCatalog(item.referencia)
  ) {
    return false
  }
  const morph = item.morphs?.find((m) => m.name === 'Regleta')
  const min = Number(item.regletaMinimo ?? morph?.min ?? 20)
  const max = Number(item.regletaMaximo ?? morph?.max ?? 120)
  const current = Number(item.regleta ?? morph?.default ?? 0)
  item.kbrLegacyRegletaPlus20Applied = true
  if (!(current > 0) || !Number.isFinite(current)) return false
  const lo = Number.isFinite(min) && min > 0 ? min : 20
  const hi = Number.isFinite(max) && max > lo ? max : 120
  const next = Math.max(lo, Math.min(hi, current + KBR_LEGACY_REGLETA_EXTRA_MM))
  item.regleta = next
  if (morph) {
    morph.default = next
    morph._default = next
  }
  return next !== current
}

export function rememberTpcUnitId(idUnidades: number | null | undefined) {
  const id = Number(idUnidades)
  if (Number.isFinite(id) && id > 0) tpcResolvedUnitIds.add(id)
}

export function isTpcResolvedUnitId(idUnidades: number | null | undefined): boolean {
  const id = Number(idUnidades)
  return Number.isFinite(id) && id > 0 && tpcResolvedUnitIds.has(id)
}

/**
 * Ya no se fuerza planificador3dapi para KBR: el GLB/unidad sale de la API
 * normal del proyecto (getProxyUrl). Las tiras Zocalo 4000 van aparte.
 */
export function resolveUnitApiBaseUrl(
  _catalogID?: string | number | null,
  _idUnidades?: number | null,
): string | null {
  return null
}

/**
 * Antes KBR omitía Zocalo 4000. Ahora KBR usa tira (recta / L frente+retorno).
 */
export function shouldSkipAutoZocalo(_catalogID: string | number | null | undefined): boolean {
  return false
}

/** @deprecated No reescribe a planner; se mantiene por compat. */
export function rewriteToPlannerCatalogUrl(url?: string | null): string | undefined {
  return url ?? undefined
}

/** @deprecated Identity: no reescribir URLs de CatalogData a planner. */
export function rewriteKbrUnitPayloadUrls<T extends Record<string, any>>(json: T): T {
  return json
}
